
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPopularCategories } from "@/utils/supabase/categoryHelpers";
import { trackHomepageEvent } from "@/services/homepageAnalytics";

const FALLBACK_QUICK_START_CATEGORIES = [
  "Handyman",
  "Cleaner",
  "Tutor",
  "Electrician",
  "Pet Care",
];

const Hero = () => {
  const { t } = useTranslation('homepage');
  const [quickStartCategories, setQuickStartCategories] = useState(FALLBACK_QUICK_START_CATEGORIES);

  useEffect(() => {
    let isMounted = true;

    const loadPopularCategories = async () => {
      try {
        const categories = await getPopularCategories(FALLBACK_QUICK_START_CATEGORIES.length);
        const categoryNames = categories
          .map((category) => category.name)
          .filter((name): name is string => Boolean(name));

        if (isMounted && categoryNames.length > 0) {
          setQuickStartCategories(categoryNames);
        }
      } catch (error) {
        console.error("Error loading hero quick start categories:", error);
      }
    };

    loadPopularCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section
      className="relative overflow-hidden bg-brand-ink text-white"
      data-test-responsive="hero"
    >
      <div
        aria-hidden="true"
        className="home-hero-backdrop absolute inset-0 bg-cover bg-center bg-no-repeat"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.12),transparent_42%),linear-gradient(135deg,rgba(9,32,53,0.46),rgba(9,32,53,0.64))]" />

      <div className="relative z-10 container mx-auto px-4 py-20 text-center md:py-28">
        <div className="mx-auto max-w-4xl">
          <h1
            className="mb-5 font-heading text-[length:var(--text-display)] font-bold leading-[0.95] tracking-tight text-white md:mb-6"
          >
            {t('hero.title')}
          </h1>
          <p
            className="mx-auto mb-8 max-w-3xl text-[length:var(--text-h3)] text-white/78 md:mb-10"
          >
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/explore"
              onClick={() => trackHomepageEvent('homepage_hero_primary_cta_click')}
              className="inline-flex min-w-[180px] items-center justify-center gap-2 rounded-md bg-white px-6 py-3.5 text-base font-semibold text-brand-ink transition-transform hover:-translate-y-0.5 hover:bg-brand-mist"
            >
              {t('hero.primaryCta')}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/submit-listing"
              onClick={() => trackHomepageEvent('homepage_hero_secondary_cta_click')}
              className="inline-flex min-w-[180px] items-center justify-center rounded-md border border-white/25 bg-white/10 px-6 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/16"
            >
              {t('hero.secondaryCta')}
            </Link>
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-white/12 bg-white/8 px-5 py-5 backdrop-blur-sm md:px-6">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.16em] text-white/65">
              {t('hero.quickBrowseLabel')}
            </p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {quickStartCategories.map((cat) => (
                <Link
                  key={cat}
                  to={`/explore?category=${encodeURIComponent(cat)}`}
                  className="rounded-full border border-white/12 bg-white/8 px-4 py-2 text-sm text-white/88 transition-colors hover:bg-white/14"
                >
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
